import { useCallback, useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import {
  preloadProductSuggestModel,
  suggestProductFromImages,
  type CategoryOption,
} from "@/lib/product-image-suggest";

type SuggestableValues = {
  name: string;
  description: string;
  category: string;
  tags: string[];
};

const flattenCategories = (input: unknown): CategoryOption[] => {
  const result: CategoryOption[] = [];
  const walk = (nodes: unknown) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      if (!node || typeof node !== "object") return;
      const item = node as { _id?: string; name?: string; children?: unknown };
      if (item._id && item.name) {
        result.push({ _id: item._id, name: item.name });
      }
      if (item.children) walk(item.children);
    });
  };
  walk(input);
  return result;
};

export function useProductImageSuggestions<T extends SuggestableValues>(
  form: UseFormReturn<T>,
  categories: unknown,
) {
  const [analyzing, setAnalyzing] = useState(false);
  const categoryOptions = useMemo(
    () => flattenCategories(categories),
    [categories],
  );

  useEffect(() => {
    preloadProductSuggestModel();
  }, []);

  const handleUploadedFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const current = {
        name: String(form.getValues("name") ?? ""),
        description: String(form.getValues("description") ?? ""),
        category: String(form.getValues("category") ?? ""),
        tags: (form.getValues("tags") as string[]) ?? [],
      };

      if (current.name.trim().length >= 2 && current.description.trim().length >= 10) {
        return;
      }

      setAnalyzing(true);
      try {
        const suggestion = await suggestProductFromImages(
          files,
          current,
          categoryOptions,
        );
        if (!suggestion) return;

        const latest = {
          name: String(form.getValues("name") ?? ""),
          description: String(form.getValues("description") ?? ""),
          category: String(form.getValues("category") ?? ""),
          tags: (form.getValues("tags") as string[]) ?? [],
        };

        if (suggestion.name && latest.name.trim().length < 2) {
          form.setValue("name" as never, suggestion.name as never, { shouldDirty: true });
        }
        if (suggestion.description && latest.description.trim().length < 10) {
          form.setValue("description" as never, suggestion.description as never, {
            shouldDirty: true,
          });
        }
        if (suggestion.category && !latest.category) {
          form.setValue("category" as never, suggestion.category as never, {
            shouldDirty: true,
          });
        }
        if (suggestion.tags?.length && latest.tags.length === 0) {
          form.setValue("tags" as never, suggestion.tags as never, { shouldDirty: true });
        }

        const applied =
          (suggestion.name && latest.name.trim().length < 2) ||
          (suggestion.description && latest.description.trim().length < 10) ||
          (suggestion.category && !latest.category) ||
          (suggestion.tags?.length && latest.tags.length === 0);

        if (applied) {
          toast.success("Suggested details from your photo. Edit anything that looks off.");
        }
      } catch (error) {
        console.error("Image suggestion failed:", error);
      } finally {
        setAnalyzing(false);
      }
    },
    [categoryOptions, form],
  );

  return { analyzing, handleUploadedFiles };
}
